import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { Subfolder } from 'modules/domains';
import { GRAPHQL_TYPENAMES } from 'modules/graphql';
import { Dispatch, SetStateAction } from 'react';
import { NoOp } from 'utils/noop';

export enum Orientation {
  Landscape = 'landscape',
  Portrait = 'portrait',
}

export enum PanelIntent {
  Browse = 'browse',
  Edit = 'edit',
  Insert = 'insert',
  Select = 'select',
  EditSelect = 'editSelect',
  Upload = 'upload',
  ImportFromUrl = 'importFromUrl',
}

export enum PanelView {
  Detail = 'detail',
  List = 'list',
  RemoteUrl = 'remoteUrl',
  Upload = 'upload',
}

export enum PromiseStatus {
  Aborted = 'aborted', // unofficial status
  Fulfilled = 'fulfilled',
  Pending = 'pending', // unofficial status
  Rejected = 'rejected',
  Undefined = 'undefined', // unofficial status (promise not initiated yet)
}

export enum ProcessImageError { // strings are in src/services/i18n/en-us/assetManager.json
  Optimize = 'optimize',
  TypeConversion = 'typeConversion',
  ReadImage = 'readImage',
}

export enum UploadAssetError { // strings are in src/services/i18n/en-us/assetManager.json
  Aborted = 'aborted',
  Failed = 'failed',
}

export enum UploadRemoteUrlError {
  FileType = 'fileType',
  InvalidImage = 'invalidImage',
  NetworkProtocol = 'networkProtocol',
  NetworkErrorImage = 'networkErrorImage',
  None = 'none',
}

export type AssetRequestConfig = {
  append?: boolean; // append results in endless scroll
  count: number; // pageSize
  offset: number; // integer (ordinal position for pagination: 0, 20, 40, ...)
  search: string;
  sortBy?: SortableField | string; // enum of field to sort by
  sortDirection?: SORT_DIRECTIONS; // enum of sort direction
};

export type FileInfo = {
  fileName: string;
  fileSize: number;
  fileType: string;
};

export enum FileType {
  BMP = 'BMP',
  GIF = 'GIF',
  JPEG = 'JPEG',
  PNG = 'PNG',
  TIFF = 'TIFF',
  WEBP = 'WEBP',
}

export type FileTypeDetails = {
  canAnimate: boolean; // is animation supported
  canSelect: boolean; // can the type be selected or dropped
  ext: string; // the extension given to this mime type (filename will be corrected to use this ext)
  mimeType: string;
  uploadKey: FileType; // the file type the image will be converted to before upload (we only allow jpeg, gif, & png officially)
};

export type FileTypes = Record<FileType, FileTypeDetails>;

export const fileTypes: FileTypes = {
  [FileType.BMP]: {
    canAnimate: false,
    canSelect: true,
    ext: 'bmp',
    mimeType: 'image/bmp',
    uploadKey: FileType.PNG,
  },
  [FileType.GIF]: {
    canAnimate: true,
    canSelect: true,
    ext: 'gif',
    mimeType: 'image/gif',
    uploadKey: FileType.GIF,
  },
  [FileType.JPEG]: {
    canAnimate: false,
    canSelect: true,
    ext: 'jpg',
    mimeType: 'image/jpeg',
    uploadKey: FileType.JPEG,
  },
  [FileType.PNG]: {
    canAnimate: false,
    canSelect: true,
    ext: 'png',
    mimeType: 'image/png',
    uploadKey: FileType.PNG,
  },
  [FileType.TIFF]: {
    canAnimate: false,
    canSelect: true,
    ext: 'tiff',
    mimeType: 'image/tiff',
    uploadKey: FileType.PNG,
  },
  [FileType.WEBP]: {
    canAnimate: true,
    canSelect: true,
    ext: 'webp',
    mimeType: 'image/webp',
    uploadKey: FileType.PNG,
  },
};

export const inputAccept = Object.values(fileTypes)
  .filter((fileType) => fileType.canSelect)
  .map((fileType) => fileType.mimeType);

export type ImageMods = {
  crop?: {
    height: number;
    unit: 'px' | '%';
    width: number;
    x: number;
    y: number;
  };
  flipH: boolean;
  flipV: boolean;
  height?: number; // in px, after crop
  rotation: number;
  width?: number; // in px, after crop
};

// new image data (dropped or selected, prior to upload)
export type NewImage = {
  id: string;
  abortUpload?: NoOp;
  selected: {
    // details of the image that was dropped/selected
    altText?: string;
    height: number; // in px
    mimeType: string;
    name: string;
    size: number; // in bytes
    width: number; // in px
    destinationFolderId?: number;
    destinationFolderName?: string;
  };
  file: {
    // the current state (client-side) of an asset
    // pending images: the image to be uploaded (before mods applied, but after corrections of selected)
    // existing assets: the current state of editable properties (name, description, etc.)
    altText?: string; // can be used for default alt attribute value
    file: File;
    height: number; // in px
    mimeType: string;
    name: string; // fixed (spaces removed, etc.), if applicable
    size: number; // in bytes
    width: number; // in px
  };
  isSelected?: boolean;
  processError?: ProcessImageError; // enum that maps to a translated string
  processStatus: PromiseStatus; // can the File be read as an appropriate file type (a rejected processStatus should have either a validationError or processError)
  upload?: ImageMods;
  uploadPct: number; // percent uploaded 0-100
  uploadError?: UploadAssetError; // enum that maps to a translated string
  uploadStatus: PromiseStatus;
  validationError?: string;
  asset?: Asset;
};

export type UpdateAssetImageRequestData = {
  assetName: string;
  size?: number;
  mimeType?: string;
  height?: number;
  width?: number;
  altText?: string;
};

export type UploadSuccess = { id: string; asset: Asset };

export type ResolveUpload = (value: UploadSuccess) => void;

export type ResolveImgData = (value: NewImage) => void;

export type RejectImgData = ({
  msg,
  error,
  imgData,
}: {
  msg: string;
  error: unknown;
  imgData: NewImage;
}) => void;

export type SetAbortControllerHandler = (id: string, abortController: AbortController) => void;

export type UploadProgressHandler = (id: string, pct: number, status?: PromiseStatus) => void;

export type UploadCompleteConfig = {
  filesToDismiss: NewImage[];
  newFiles: NewImage[];
  removeSelectedUploads: (ids: string[]) => void;
  setPendingImages: Dispatch<SetStateAction<NewImage[]>>;
  uploadFailures: Record<string, any>[];
  uploadSuccesses: UploadSuccess[];
};

export enum TakeoverIntent {
  EDIT = 'edit',
  UPLOAD = 'upload',
  IMPORT_FROM_URL = 'importFromUrl',
  BROWSE_FILES = 'browseFiles',
}

export enum ImageAttrs {
  ALT_DESC = 'alt',
  HEIGHT = 'height',
  SOURCE = 'src',
  STYLES = 'style',
  WIDTH = 'width',
}

export enum ImageAttrBatch {
  SET_NEW = 'new',
  UPDATE_BATCH = 'update',
}

export type ImageAttrState = {
  [ImageAttrs.ALT_DESC]: string;
  [ImageAttrs.HEIGHT]: string;
  [ImageAttrs.SOURCE]: string;
  [ImageAttrs.STYLES]: string;
  [ImageAttrs.WIDTH]: string;
};

export type ImageAttrAction =
  | { type: ImageAttrs; value: string }
  | { type: ImageAttrBatch; value: Partial<ImageAttrState> };

export type ImageMetadata = {
  assetName?: string;
  altText?: string;
  createdAt?: string;
  creatorName?: string;
  height: number; // in px
  mimeType?: string;
  size?: number; // in bytes
  width: number; // in px
};

export enum AssetType {
  Image = 'Image',
}

export type AssetSubfolder = Subfolder<GRAPHQL_TYPENAMES.ASSET_SUBFOLDER>;

// BE Types: It is important that these types match the types used in the BE
export type Asset = {
  __typename: GRAPHQL_TYPENAMES;
  id: number;
  projectId: number;
  assetName: string;
  altText?: string;
  assetType: AssetType;
  campaigns?: number;
  createdAt: string;
  createdBy?: string; // email address (TBD: what is the value in UUID-based projects)
  creatorUser?: User;
  updatedAt: string;
  updatedBy: string; // email address (TBD: what is the value in UUID-based projects)
  updatedByUser?: User; // Needs to be added to the DB
  url: string;
  description?: string; // can be used to auto-populate the alt attribute in an image tag
  size: number; // in bytes
  height: number; // in px
  width: number; // in px
  mimeType: string;
};

// libs/creative-library/src/main/scala/com/iterable/creativelibrary/persistence/assets/ManagedAssetsRepository.scala
export enum SortableField {
  CreatedAt = 'CreatedAt',
  Id = 'Id',
  UpdatedAt = 'UpdatedAt',
}

export type User = {
  id: string;
  fullName: string;
  avatarUrl?: string;
};

export type Info = {
  count: number;
  limit: number;
  offset: number;
  page: number;
};

export type Pagination = {
  offset: number;
  limit: number;
};

export type Sort = {
  sortBy: SortableField;
  sortDirection: SORT_DIRECTIONS;
};

export enum SortBy {
  Name = 'Name',
  UpdatedAt = 'UpdatedAt',
  Size = 'Size',
  Type = 'MimeType',
}

export enum DropZoneArea {
  Index = 'index',
  None = 'none',
  Panel = 'panel',
  Takeover = 'takeover',
}

export enum CreativeLibraryFilterPrefix {
  FileType = 'file-type-',
  FileSize = 'file-size-',
  Creator = 'creator-',
  ModifiedBy = 'modified-by-',
}

export enum CreativeLibraryFilterId {
  FileType = 'File Type',
  FileSize = 'File Size',
  Creator = 'Creator',
  ModifiedBy = 'Modified by',
}

export type FilterOption = string | number;
export type Filters = FilterOption[];
